import React, { useEffect, useMemo, useState } from 'react';
import qrphLogo from '../../assets/qrph.png';
// alipayLogo removed — not supported by PayMongo

import { API_BASE } from '../../config/runtime';

// ── Helpers ────────────────────────────────────────────────────────────────
const normalizeContact = (v) => (v || '').replace(/\D/g, '').slice(0, 11);
const fmt = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toLocaleString() : '0'; };
const roundCurrency = (v) => Math.round((Number(v) || 0) * 100) / 100;
const formatDuration = (mins) => {
  if (!mins) return null;
  if (mins < 60) return `${mins} min`;
  const h = mins / 60;
  return `${h % 1 === 0 ? h : h.toFixed(1)}h`;
};
const QR_PH_GENERATION_ERROR_MESSAGE = 'Unable to generate QR code. Please check your internet connection or try again later.';

const createPaymentError = (code, userMessage, technicalMessage, meta = {}) => {
  const error = new Error(technicalMessage || userMessage);
  error.code = code;
  error.userMessage = userMessage;
  Object.assign(error, meta);
  return error;
};

const getUserFacingPaymentError = (error, paymentMethod) => {
  if (
    paymentMethod === 'qrph'
    && ['NETWORK', 'SERVER', 'INVALID_RESPONSE', 'API'].includes(error?.code)
  ) {
    return QR_PH_GENERATION_ERROR_MESSAGE;
  }
  return error?.userMessage || error?.message || 'An error occurred. Please try again.';
};

const getPaymentStatusCopy = (status) => {
  switch (status) {
    case 'paid':
      return { label: 'Payment Successful', tone: 'text-emerald-600', message: 'Payment received successfully.' };
    case 'processing':
      return { label: 'Checking Payment', tone: 'text-[#8b5e3c]', message: 'Your payment is being verified.' };
    case 'awaiting_next_action':
    case 'awaiting_payment_method':
    case 'awaiting_payment':
    case 'unpaid':
    case '':
      return { label: 'Not Paid Yet', tone: 'text-amber-600', message: 'Waiting for payment confirmation.' };
    default:
      return { label: 'Not Paid Yet', tone: 'text-amber-600', message: 'Waiting for payment confirmation.' };
  }
};

// ── Time slots ─────────────────────────────────────────────────────────────
const TIME_SLOTS = [
  { value: '06:00', label: '6:00 AM' }, { value: '06:30', label: '6:30 AM' },
  { value: '07:00', label: '7:00 AM' }, { value: '07:30', label: '7:30 AM' },
  { value: '08:00', label: '8:00 AM' }, { value: '08:30', label: '8:30 AM' },
  { value: '09:00', label: '9:00 AM' }, { value: '09:30', label: '9:30 AM' },
  { value: '10:00', label: '10:00 AM' }, { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' }, { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' }, { value: '12:30', label: '12:30 PM' },
  { value: '13:00', label: '1:00 PM' }, { value: '13:30', label: '1:30 PM' },
  { value: '14:00', label: '2:00 PM' }, { value: '14:30', label: '2:30 PM' },
  { value: '15:00', label: '3:00 PM' }, { value: '15:30', label: '3:30 PM' },
  { value: '16:00', label: '4:00 PM' }, { value: '16:30', label: '4:30 PM' },
  { value: '17:00', label: '5:00 PM' }, { value: '17:30', label: '5:30 PM' },
  { value: '18:00', label: '6:00 PM' }, { value: '18:30', label: '6:30 PM' },
  { value: '19:00', label: '7:00 PM' }, { value: '19:30', label: '7:30 PM' },
  { value: '20:00', label: '8:00 PM' }, { value: '20:30', label: '8:30 PM' },
  { value: '21:00', label: '9:00 PM' }, { value: '21:30', label: '9:30 PM' },
  { value: '22:00', label: '10:00 PM' },
];

const WORKING_HOURS = {
  monday:    { start: '10:00', end: '20:00' },
  tuesday:   { start: '10:00', end: '20:00' },
  wednesday: { start: '10:00', end: '20:00' },
  thursday:  { start: '10:00', end: '20:00' },
  friday:    { start: '10:00', end: '20:00' },
  saturday:  { start: '10:00', end: '20:00' },
  sunday:    { start: '09:00', end: '20:00' },
};

const parseTimeToHH = (str) => {
  if (!str) return null;
  const s = str.trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':').map(Number);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const m12 = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(s);
  if (m12) {
    let h = Number(m12[1]);
    const mins = m12[2];
    const meridiem = m12[3].toUpperCase();
    if (meridiem === 'AM' && h === 12) h = 0;
    if (meridiem === 'PM' && h !== 12) h += 12;
    return `${String(h).padStart(2, '0')}:${mins}`;
  }
  return null;
};

const normalizeWorkingHours = (workingHoursRaw) => {
  if (!workingHoursRaw) return null;
  if (typeof workingHoursRaw === 'object') return workingHoursRaw;

  const raw = String(workingHoursRaw).trim();
  if (!raw) return null;

  // 1) JSON
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}

  const dayNames = Object.keys(WORKING_HOURS);

  // Time pattern: "8:30", "8:30 AM", "8:30AM"
  const TIME_PAT = `\\d{1,2}:\\d{2}(?:\\s*[AP]M)?`;
  // Separator between two times: " - ", "-", "–", " to "
  const SEP_PAT = `\\s*[-–]\\s*|\\s+to\\s+`;

  // 2) Per-day: "monday 8:30 AM - 6:30 PM, tuesday 9:00-18:00"
  const perDayOut = {};
  const parts = raw.split(/[,;]+/g).map(p => p.trim()).filter(Boolean);
  for (const part of parts) {
    const m = new RegExp(
      `(${dayNames.join('|')})\\s*:?\\s*(${TIME_PAT})(${SEP_PAT})(${TIME_PAT})`,
      'i'
    ).exec(part);
    if (!m) continue;
    const day = m[1].toLowerCase();
    const start = parseTimeToHH(m[2].trim());
    const end = parseTimeToHH(m[4].trim());
    if (start && end) perDayOut[day] = { start, end };
  }
  if (Object.keys(perDayOut).length) return perDayOut;

  // 3) Universal: "7:30 AM - 6:00 PM" → all days
  const universal = new RegExp(`^(${TIME_PAT})(${SEP_PAT})(${TIME_PAT})$`, 'i').exec(raw);
  if (universal) {
    const start = parseTimeToHH(universal[1].trim());
    const end = parseTimeToHH(universal[3].trim());
    if (start && end) {
      return Object.fromEntries(dayNames.map(day => [day, { start, end }]));
    }
  }

  return null;
};

const getAllowedTimeSlotsForBranch = (dateString, branchId, branchesList) => {
  const dayKey = getDayKey(dateString);
  if (!dayKey) return TIME_SLOTS;

  const branch = branchesList?.find(b => String(b.branch_id) === String(branchId));
  const fallbackSchedule = WORKING_HOURS[dayKey];
  const scheduleFromBranch = normalizeWorkingHours(branch?.working_hours);
  const schedule = scheduleFromBranch?.[dayKey] || fallbackSchedule;

  if (!schedule) return TIME_SLOTS;

  const min = toMinutes(schedule.start);
  const max = toMinutes(schedule.end);

  return TIME_SLOTS.filter(slot => {
    const minutes = toMinutes(slot.value);
    return minutes >= min && minutes <= max; // ← inclusive: shows closing time slot
  });
};

const PAYMENT_METHODS = [
  { value: 'qrph', label: 'QR Ph', icon: qrphLogo, type: 'ewallet' },
];
const DEPOSIT_RATE = 0.5;

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const toMinutes = (time) => {
  const [h, m] = (time || '00:00').split(':').map(Number);
  return h * 60 + m;
};

const getDayKey = (dateString) => {
  if (!dateString) return null;
  const date = new Date(`${dateString}T00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return DAY_NAMES[date.getDay()];
};



// ── Service Selector Overlay ───────────────────────────────────────────────
const ServiceSelector = ({ onClose, filteredServices, groupedServices, selectedServices, onToggle, totalAmount }) => (
  <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4">
    <div className="w-full sm:max-w-lg bg-white rounded-t-[2rem] sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '92svh' }}>
      <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
        <div className="w-10 h-1 rounded-full bg-gray-200" />
      </div>
      <div className="px-5 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-[#f0e8e0] flex items-center justify-between flex-shrink-0">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#c4956a]">Hygea Wellness</p>
          <h2 className="text-lg sm:text-xl font-bold text-[#1a1009] mt-0.5">Choose Services</h2>
        </div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f5ede4] text-[#8b5e3c] flex items-center justify-center hover:bg-[#ecddd0] transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {filteredServices.length === 0 ? (
          <div className="text-center py-16 text-[#a89080] text-sm">No services available for this branch.</div>
        ) : (
          Object.entries(groupedServices).map(([cat, svcs]) => (
            <div key={cat}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-[#ede0d4]" />
                <span className="text-[9px] font-black uppercase tracking-[0.4em] text-[#c4956a]">{cat}</span>
                <div className="h-px flex-1 bg-[#ede0d4]" />
              </div>
              <div className="space-y-2">
                {svcs.map(svc => {
                  const selected = Boolean(selectedServices.find(s => s.id === svc.id));
                  return (
                    <button key={svc.id} type="button" onClick={() => onToggle(svc)}
                      className={`w-full flex items-center gap-3 sm:gap-4 p-3 rounded-2xl border-2 transition-all text-left ${selected ? 'border-[#8b5e3c] bg-[#fdf5ee] shadow-sm' : 'border-[#f0e8e0] bg-white hover:border-[#d4a882]'}`}
                    >
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden flex-shrink-0 ${selected ? 'ring-2 ring-[#8b5e3c]' : ''}`}>
                        {svc.image
                          ? <img src={svc.image} alt={svc.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-[#f0e4d8] flex items-center justify-center text-[8px] text-[#c4956a] font-bold uppercase">H</div>
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1a1009] truncate">{svc.name}</p>
                        {formatDuration(svc.duration_minutes) && (
                          <p className="text-[11px] text-[#a89080] mt-0.5">{formatDuration(svc.duration_minutes)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <span className={`text-sm font-black ${selected ? 'text-[#8b5e3c]' : 'text-[#1a1009]'}`}>₱{fmt(svc.price)}</span>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${selected ? 'bg-[#8b5e3c] border-[#8b5e3c]' : 'border-[#d4c4b8]'}`}>
                          {selected && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="px-4 sm:px-5 py-4 bg-[#fdf8f4] border-t border-[#f0e8e0] flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-[#a89080] uppercase tracking-wider">
            {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} selected
          </span>
          <span className="text-base font-black text-[#8b5e3c]">₱{fmt(totalAmount)}</span>
        </div>
        <button onClick={onClose} disabled={selectedServices.length === 0}
          className={`w-full py-3.5 rounded-2xl font-black text-sm uppercase tracking-[0.15em] transition-all ${selectedServices.length > 0 ? 'bg-[#8b5e3c] text-white hover:bg-[#7a5234] shadow-lg shadow-[#8b5e3c]/25' : 'bg-[#ede0d4] text-[#c4a894] cursor-not-allowed'}`}
        >
          {selectedServices.length > 0 ? `Confirm ${selectedServices.length} Service${selectedServices.length > 1 ? 's' : ''}` : 'Select a service'}
        </button>
      </div>
    </div>
  </div>
);

// ── Order Summary (collapsible on mobile) ─────────────────────────────────
const OrderSummary = ({ branchSelected, selectedBranchLabel, selectedServices, form, totalAmount, totalDuration, selectedPaymentMethod }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-[#1c0f07] sm:hidden flex-shrink-0">
      <button type="button" onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/50">Your Appointment</span>
          {selectedServices.length > 0 && (
            <span className="bg-[#c4956a]/20 text-[#c4956a] text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
              {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-base font-black text-[#c4956a]">₱{fmt(totalAmount)}</span>
          <svg className={`w-4 h-4 text-white/40 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {expanded && (
        <div className="px-5 pb-4 space-y-3 border-t border-white/10 pt-3">
          {branchSelected && selectedBranchLabel && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-0.5">Location</p>
              <p className="text-xs font-semibold text-white/90">{selectedBranchLabel}</p>
            </div>
          )}
          {selectedServices.length > 0 && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Services</p>
              <div className="space-y-1.5">
                {selectedServices.map((s, i) => (
                  <div key={i} className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-white/85 leading-tight truncate flex-1">{s.name}</p>
                    <span className="text-xs font-bold text-[#c4956a] flex-shrink-0">₱{fmt(s.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(form.preferredDate || form.preferredTime) && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-0.5">Schedule</p>
              {form.preferredDate && <p className="text-xs font-semibold text-white/85">{new Date(form.preferredDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>}
              {form.preferredTime && <p className="text-xs text-white/60 mt-0.5">{TIME_SLOTS.find(t => t.value === form.preferredTime)?.label || form.preferredTime}</p>}
            </div>
          )}
          {selectedPaymentMethod && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-0.5">Payment</p>
              <p className="text-xs font-semibold text-white/85">
                {PAYMENT_METHODS.find(m => m.value === selectedPaymentMethod)?.label}
              </p>
            </div>
          )}
          {totalDuration > 0 && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-0.5">Est. Duration</p>
              <p className="text-xs font-semibold text-white/85">{formatDuration(totalDuration * form.numberOfClients)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Payment Step ───────────────────────────────────────────────────────────
const PaymentStep = ({
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  totalAmount,
  paymentAmount,
  processingPayment,
  paymentResult,
  onRefreshPaymentStatus,
  onRetryQrPh,
  onClose
}) => {
  const activeMethod = PAYMENT_METHODS.find(m => m.value === selectedPaymentMethod);
  const paymentPaid = paymentResult.status === 'paid';
  const showQrCode = Boolean(paymentResult.qrCode && selectedPaymentMethod === 'qrph' && !paymentPaid);
  const paymentStatusCopy = getPaymentStatusCopy(paymentResult.status);

  // Show success screen when payment is complete
  if (paymentPaid) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <div className="mb-6">
          <svg className="w-20 h-20 mx-auto text-emerald-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-[#1a1009] mb-2">Payment Successful!</h2>
        <p className="text-[#a89080] mb-8">Your booking has been confirmed.</p>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center px-8 py-3.5 rounded-2xl bg-[#8b5e3c] text-white font-black text-sm uppercase tracking-[0.15em] hover:bg-[#7a5234] transition-colors shadow-lg shadow-[#8b5e3c]/25"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.45em] text-[#c4956a]">Step 2 of 2</p>
        <h3 className="text-xl font-bold text-[#1a1009] mt-1">Payment Method</h3>
        <p className="text-sm text-[#a89080] mt-1">Pay the 50% down payment using your preferred e-wallet to reserve this booking.</p>
      </div>

      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#8b6b52] mb-3">E-Wallet</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PAYMENT_METHODS.map(method => (
            <button
              key={method.value}
              type="button"
              onClick={() => setSelectedPaymentMethod(method.value)}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl border-2 transition-all text-left ${
                selectedPaymentMethod === method.value
                  ? 'border-[#8b5e3c] bg-[#fdf5ee]'
                  : 'border-[#e8d9cc] bg-white hover:border-[#c4a882] hover:bg-[#fdfaf7]'
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-white border border-[#f0e8e0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={method.icon} alt={method.label} className="w-7 h-7 object-contain" />
              </div>
              <span className="text-xs font-semibold text-[#1a1009] leading-tight truncate flex-1">{method.label}</span>
              {selectedPaymentMethod === method.value && (
                <svg className="w-3.5 h-3.5 text-[#8b5e3c] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      {activeMethod && (
        <div className="rounded-2xl border border-[#e8d9cc] bg-white p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-[#f0e8e0] flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={activeMethod.icon} alt={activeMethod.label} className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#1a1009]">{activeMethod.label}</p>
              <p className="text-xs text-[#8b6b52]">Down payment (50%): PHP {fmt(paymentAmount)}</p>
              <p className="text-[11px] text-[#a89080] mt-0.5">Total service cost: PHP {fmt(totalAmount)}</p>
            </div>
          </div>

          {selectedPaymentMethod === 'qrph' && (
            <div className="mt-4 rounded-2xl bg-[#fdf7f1] border border-[#f1e5d9] p-4">
              <p className="text-xs text-[#6b4e38]">Generate a one-time QR Ph code here, let the customer scan it with their bank or wallet app, then refresh the payment status inside this modal.</p>
            </div>
          )}

          {paymentResult.error && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-xs font-semibold text-red-600">{paymentResult.error}</p>
              {selectedPaymentMethod === 'qrph' && (
                <button
                  type="button"
                  onClick={onRetryQrPh}
                  disabled={processingPayment}
                  className="mt-3 inline-flex items-center justify-center rounded-xl border border-red-200 bg-white px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Retry QR Generation
                </button>
              )}
            </div>
          )}

          {paymentResult.message && !paymentResult.error && (
            <div className="mt-4 rounded-2xl border border-[#eadbce] bg-[#fdf8f3] px-4 py-3">
              <p className="text-xs font-semibold text-[#6b4e38]">{paymentResult.message}</p>
            </div>
          )}

          {showQrCode && (
            <div className="mt-4 rounded-3xl border border-[#eadbce] bg-[#fffaf6] p-5 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#c4956a]">Scan To Pay</p>
              <div className="mx-auto mt-4 w-full max-w-[240px] rounded-3xl bg-white p-4 shadow-sm">
                <img src={paymentResult.qrCode} alt={paymentResult.qrLabel || 'QR Ph code'} className="w-full h-auto" />
              </div>
              <p className="mt-3 text-sm font-bold text-[#1a1009]">{paymentResult.qrLabel || 'QR Ph'}</p>
              <p className="text-xs text-[#8b6b52] mt-1">After the customer pays, refresh the payment status below.</p>
              <div className="mt-4 p-3 rounded-xl bg-[#faf4ee] border border-[#eadbce]">
                <p className="text-xs font-semibold text-[#6b4e38]">
                  Payment Status: <span className={`${paymentStatusCopy.tone} font-black`}>{paymentStatusCopy.label}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onRefreshPaymentStatus}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#8b5e3c] text-white text-xs font-black uppercase tracking-[0.15em] hover:bg-[#7a5234] transition-colors"
              >
                Check Payment Status
              </button>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-[#f5ede4] flex items-center gap-2">
            {processingPayment ? (
              <>
                <svg className="animate-spin w-4 h-4 text-[#8b5e3c]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-[10px] text-[#8b5e3c] font-semibold">
                  {showQrCode
                    ? 'Generating QR code...'
                    : 'Checking payment status...'}
                </p>
              </>
            ) : paymentPaid ? (
              <>
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-[10px] text-emerald-700 font-semibold">Payment received. Your booking is confirmed.</p>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5 text-[#c4956a] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-[10px] text-[#a89080]">
                  {paymentResult.bookingId
                    ? `Status: ${paymentStatusCopy.label}. ${paymentStatusCopy.message}`
                    : 'Payments are securely handled by PayMongo via your selected e-wallet.'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {!selectedPaymentMethod && (
        <p className="text-[10px] text-amber-500 text-center">Select a payment method to continue.</p>
      )}
    </div>
  );
};

// ── Main BookingModal ──────────────────────────────────────────────────────
const BookingModal = ({
  isOpen, onClose,
  selectedServices: propSelectedServices,
  user, isLoggedIn, onBookingComplete,
  defaultBranchId,
  services: propServices, onServicesChange,
  selectionMode: propSelectionMode, onSelectionModeChange
}) => {
  const [internalServices, setInternalServices] = useState([]);
  const [internalSelectedServices, setInternalSelectedServices] = useState([]);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('details');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentResult, setPaymentResult] = useState({
    bookingId: null,
    paymentId: '',
    status: '',
    message: '',
    error: '',
    qrCode: '',
    qrLabel: '',
    checkoutUrl: '',
    sessionSignature: ''
  });
  const [paymentCompletionHandled, setPaymentCompletionHandled] = useState(false);

  const [form, setForm] = useState({
    fullName: '', contactNumber: '', guestEmail: '',
    numberOfClients: 1, branchId: '', preferredDate: '', preferredTime: '',
    suggestion: ''
  });

  const isExternalMode = propServices !== undefined && onServicesChange !== undefined;
  const serviceCatalog = useMemo(
    () => (isExternalMode ? (propServices || []) : internalServices),
    [isExternalMode, propServices, internalServices]
  );
  const selectedServices = useMemo(
    () => (isExternalMode ? (propSelectedServices || []) : internalSelectedServices),
    [isExternalMode, propSelectedServices, internalSelectedServices]
  );

  const updateSelected = (next) => {
    isExternalMode && typeof onServicesChange === 'function'
      ? onServicesChange(next) : setInternalSelectedServices(next);
  };

  useEffect(() => {
    if (!isExternalMode) setInternalSelectedServices(propSelectedServices || []);
  }, [propSelectedServices, isExternalMode]);

  useEffect(() => {
    if (!isOpen) {
      setShowServiceSelector(false);
      setCurrentStep('details');
      setSelectedPaymentMethod('');
      setForm(prev => ({ ...prev, contactNumber: '', guestEmail: '' }));
      setPaymentResult({
        bookingId: null,
        paymentId: '',
        status: '',
        message: '',
        error: '',
        qrCode: '',
        qrLabel: '',
        checkoutUrl: '',
        sessionSignature: ''
      });
      setPaymentCompletionHandled(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isLoggedIn || !user) return;
    const profileContact = normalizeContact(user.phone_number || user.contact_number || user.phone || user.contact || '');
    setForm(prev => ({
      ...prev,
      fullName:      prev.fullName      || user.full_name || user.name || '',
      contactNumber: prev.contactNumber || profileContact,
    }));
  }, [isOpen, isLoggedIn, user]);

  useEffect(() => {
    if (!isOpen || !defaultBranchId || form.branchId) return;
    setForm(prev => ({ ...prev, branchId: String(defaultBranchId) }));
  }, [isOpen, defaultBranchId, form.branchId]);

  const branchSelected = Boolean(form.branchId);

  const selectedBranchLabel = useMemo(() => {
    if (!branchSelected) return '';
    const b = branches.find(b => String(b.branch_id) === String(form.branchId));
    return b ? `${b.branch_name}${b.address ? `, ${b.address}` : ''}` : '';
  }, [branchSelected, branches, form.branchId]);

  const allowedCategoryIds = useMemo(() => {
    if (!branchSelected || !categories.length) return null;
    const bid = Number(form.branchId);
    return categories
      .filter(c => { const ids = Array.isArray(c.branch_ids) ? c.branch_ids : []; return !ids.length || ids.some(id => Number(id) === bid); })
      .map(c => Number(c.category_id));
  }, [branchSelected, categories, form.branchId]);

  const filteredServices = useMemo(() => {
    if (!branchSelected || !serviceCatalog.length) return [];
    const bid = Number(form.branchId);
    return serviceCatalog.filter(s => {
      const bids = Array.isArray(s.branch_ids) ? s.branch_ids : [];
      if (bids.length && !bids.some(id => Number(id) === bid)) return false;
      if (!allowedCategoryIds) return true;
      return allowedCategoryIds.includes(Number(s.category_id));
    });
  }, [branchSelected, serviceCatalog, allowedCategoryIds, form.branchId]);

  const groupedFilteredServices = useMemo(() => filteredServices.reduce((acc, s) => {
    const cat = s.category_name || 'General Services';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {}), [filteredServices]);

  const allowedTimeSlots = useMemo(
    () => getAllowedTimeSlotsForBranch(form.preferredDate, form.branchId, branches),
    [form.preferredDate, form.branchId, branches]
  );

  // When the client changes branch/date, ensure any previously selected time
  // is still available for the new schedule.
  useEffect(() => {
    if (!form.preferredDate || !form.preferredTime) return;
    if (!allowedTimeSlots.some(slot => slot.value === form.preferredTime)) {
      setForm(prev => ({ ...prev, preferredTime: '' }));
    }
  }, [form.preferredDate, form.preferredTime, allowedTimeSlots]);

  // Ensure server-side validation uses the same branch hours as the dropdown
  const isTimeAllowedForSelectedBranch = useMemo(() => {
    if (!form.preferredTime || !form.preferredDate) return true;
    const allowedSet = new Set(allowedTimeSlots.map(s => s.value));
    return allowedSet.has(form.preferredTime);
  }, [form.preferredTime, form.preferredDate, allowedTimeSlots]);



  useEffect(() => {
    if (!form.preferredDate || !form.preferredTime) return;
    if (!allowedTimeSlots.some(slot => slot.value === form.preferredTime)) {
      setForm(prev => ({ ...prev, preferredTime: '' }));
    }
  }, [form.preferredDate, form.preferredTime, allowedTimeSlots]);

  const canProceed = branchSelected && selectedServices.length > 0;
  const totalAmount = roundCurrency(selectedServices.reduce((sum, s) => sum + Number(s.price), 0) * form.numberOfClients);
  const paymentAmount = roundCurrency(totalAmount * DEPOSIT_RATE);
  const totalDuration = selectedServices.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);
  const bookingDraftSignature = useMemo(() => JSON.stringify({
    branchId: String(form.branchId || ''),
    preferredDate: form.preferredDate || '',
    preferredTime: form.preferredTime || '',
    numberOfClients: Number(form.numberOfClients) || 1,
    selectedPaymentMethod,
    services: selectedServices.map(s => `${s.id}:${Number(s.price) || 0}`).sort(),
    totalAmount,
    paymentAmount,
  }), [
    form.branchId,
    form.preferredDate,
    form.preferredTime,
    form.numberOfClients,
    selectedPaymentMethod,
    selectedServices,
    totalAmount,
    paymentAmount,
  ]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const guestEmail = form.guestEmail.trim();
  const guestEmailMissing = !isLoggedIn && !guestEmail;
  const emailError = !isLoggedIn && !!guestEmail && !isValidEmail(guestEmail);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const [br, cr] = await Promise.all([fetch(`${API_BASE}/get_branches.php`), fetch(`${API_BASE}/manage_categories.php`)]);
        const [bd, cd] = await Promise.all([br.json(), cr.json()]);
        if (bd.success) setBranches(bd.branches || []);
        if (cd.success) setCategories(cd.categories || []);
      } catch { setBranches([]); setCategories([]); }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isExternalMode) return;
    fetch(`${API_BASE}/get_services.php`).then(r => r.json())
      .then(d => { if (d.success) setInternalServices(d.services || []); })
      .catch(() => { });
  }, [isOpen, isExternalMode]);

  useEffect(() => {
    if (isOpen && isExternalMode && branchSelected && filteredServices.length > 0 && selectedServices.length === 0) {
      setShowServiceSelector(true);
    }
  }, [isOpen, isExternalMode, branchSelected, filteredServices.length, selectedServices.length]);

  useEffect(() => {
    if (!isOpen || !branchSelected) return;
    const allowed = new Set(filteredServices.map(s => s.id));
    const next = selectedServices.filter(s => allowed.has(s.id));
    if (next.length !== selectedServices.length) {
      isExternalMode && typeof onServicesChange === 'function'
        ? onServicesChange(next)
        : setInternalSelectedServices(next);
    }
  }, [isOpen, branchSelected, filteredServices, selectedServices, isExternalMode, onServicesChange]);

  const resetForm = () => {
    setForm({
      fullName:      isLoggedIn ? (user?.full_name || user?.name || '') : '',
      contactNumber: isLoggedIn ? normalizeContact(user?.phone_number || user?.contact_number || user?.phone || user?.contact || '') : '',
      guestEmail: '',
      numberOfClients: 1, branchId: '', preferredDate: '', preferredTime: ''
    });
    setCurrentStep('details');
    setSelectedPaymentMethod('');
    setPaymentResult({
      bookingId: null,
      paymentId: '',
      status: '',
      message: '',
      error: '',
      qrCode: '',
      qrLabel: '',
      checkoutUrl: '',
      sessionSignature: ''
    });
    setPaymentCompletionHandled(false);
    updateSelected([]);
  };

  useEffect(() => {
    if (!paymentResult.bookingId || paymentResult.status !== 'paid' || paymentCompletionHandled) return;
    setPaymentCompletionHandled(true);
    if (onBookingComplete) onBookingComplete();
  }, [paymentResult.bookingId, paymentResult.status, paymentCompletionHandled, onBookingComplete]);

  const createBookingRecord = async () => {
    const primary = selectedServices[0];
    const payloadBase = {
      branch_id:         form.branchId,
      service_id:        primary.id,
      appointment_date:  form.preferredDate,
      appointment_time:  form.preferredTime,
      number_of_clients: form.numberOfClients,
      payment_method:    selectedPaymentMethod,
      total_price:       totalAmount,
      services: selectedServices.map(s => ({
        service_id:       s.id,
        name:             s.name,
        duration_minutes: s.duration_minutes,
        price:            s.price
      })),
    };

    const payload = isLoggedIn && user
      ? { client_id: user.user_id, ...payloadBase }
      : { guest_name: form.fullName, guest_contact: form.contactNumber, guest_email: guestEmail, ...payloadBase };

    let res;
    try {
      res = await fetch(`${API_BASE}/create_booking.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      throw createPaymentError(
        'NETWORK',
        selectedPaymentMethod === 'qrph'
          ? QR_PH_GENERATION_ERROR_MESSAGE
          : 'Unable to create your booking. Please check your internet connection and try again.',
        error?.message || 'Network error while creating booking.',
        { originalError: error, payload }
      );
    }

    const rawBody = await res.text();
    let result = null;
    if (rawBody) {
      try {
        result = JSON.parse(rawBody);
      } catch (error) {
        throw createPaymentError(
          'INVALID_RESPONSE',
          selectedPaymentMethod === 'qrph'
            ? QR_PH_GENERATION_ERROR_MESSAGE
            : 'Booking service returned an invalid response. Please try again.',
          'Invalid JSON response from create booking API.',
          { status: res.status, rawBody, originalError: error, payload }
        );
      }
    }

    const pendingPaymentId = result?.pending_payment_id || result?.booking_id;

    if (!res.ok || !result?.success || !pendingPaymentId) {
      throw createPaymentError(
        'API',
        selectedPaymentMethod === 'qrph'
          ? QR_PH_GENERATION_ERROR_MESSAGE
          : (result?.message || 'Failed to create booking.'),
        result?.message || `Create booking API failed with HTTP ${res.status}.`,
        { status: res.status, responseBody: result, rawBody, payload }
      );
    }

    setPaymentResult(prev => ({
      ...prev,
      bookingId: pendingPaymentId,
      message: 'Payment session created. Continue with payment below.',
      error: '',
      sessionSignature: bookingDraftSignature
    }));

    return pendingPaymentId;
  };

  const processPayment = async (pendingPaymentId) => {
    const requestPayload = {
      amount: paymentAmount,
      pending_payment_id: pendingPaymentId,
      payment_method: selectedPaymentMethod
    };

    let res;
    try {
      res = await fetch(`${API_BASE}/process_payment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });
    } catch (error) {
      throw createPaymentError(
        'NETWORK',
        selectedPaymentMethod === 'qrph'
          ? QR_PH_GENERATION_ERROR_MESSAGE
          : 'Unable to reach payment service. Please check your internet connection and try again.',
        error?.message || 'Network error while processing payment.',
        { originalError: error, requestPayload }
      );
    }

    const rawBody = await res.text();
    let result = null;
    if (rawBody) {
      try {
        result = JSON.parse(rawBody);
      } catch (error) {
        throw createPaymentError(
          'INVALID_RESPONSE',
          selectedPaymentMethod === 'qrph'
            ? QR_PH_GENERATION_ERROR_MESSAGE
            : 'Payment service returned an invalid response. Please try again.',
          'Invalid JSON response from payment API.',
          { status: res.status, rawBody, originalError: error, requestPayload }
        );
      }
    }

    if (!res.ok) {
      const technicalMessage = result?.message || `Payment API failed with HTTP ${res.status}.`;
      if (res.status >= 500) {
        throw createPaymentError(
          'SERVER',
          selectedPaymentMethod === 'qrph'
            ? QR_PH_GENERATION_ERROR_MESSAGE
            : 'Payment server is temporarily unavailable. Please try again later.',
          technicalMessage,
          { status: res.status, responseBody: result, rawBody, requestPayload }
        );
      }
      throw createPaymentError(
        'API',
        selectedPaymentMethod === 'qrph'
          ? QR_PH_GENERATION_ERROR_MESSAGE
          : (result?.message || 'Unable to process payment request.'),
        technicalMessage,
        { status: res.status, responseBody: result, rawBody, requestPayload }
      );
    }

    if (!result || typeof result !== 'object' || result.success !== true) {
      throw createPaymentError(
        'INVALID_RESPONSE',
        selectedPaymentMethod === 'qrph'
          ? QR_PH_GENERATION_ERROR_MESSAGE
          : 'Payment service returned an unexpected response. Please try again.',
        'Missing or unsuccessful payment payload.',
        { status: res.status, responseBody: result, rawBody, requestPayload }
      );
    }

    if (selectedPaymentMethod === 'qrph' && !result.qr_code) {
      throw createPaymentError(
        'INVALID_RESPONSE',
        QR_PH_GENERATION_ERROR_MESSAGE,
        'QR Ph response did not include qr_code.',
        { status: res.status, responseBody: result, rawBody, requestPayload }
      );
    }

    return result;
  };

  const refreshPaymentStatus = async (silent = false) => {
    if (!paymentResult.bookingId) return null;
    if (!silent) setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/get_payment_status.php?pending_payment_id=${paymentResult.bookingId}`);
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Unable to refresh payment status.');
      }

      setPaymentResult(prev => ({
        ...prev,
        bookingId: result.booking_id || prev.bookingId,
        status: result.payment_status || prev.status,
        paymentId: result.payment_id || prev.paymentId,
        qrCode: result.qr_code || (result.payment_status === 'awaiting_payment_method' ? '' : prev.qrCode),
        message: result.payment_status === 'paid'
          ? 'Payment Successful'
          : result.payment_status === 'awaiting_payment_method'
            ? 'This payment attempt is no longer active. You can generate a new one from this modal.'
            : 'Not Paid Yet',
        error: ''
      }));

      return result;
    } catch (error) {
      if (!silent) {
        setPaymentResult(prev => ({ ...prev, error: error.message || 'Unable to refresh payment status.' }));
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || currentStep !== 'payment' || !paymentResult.bookingId) return undefined;
    
    // Auto-poll for QR Ph, card, and wallet payments awaiting confirmation
    const shouldAutoPoll = [
      'awaiting_next_action',
      'processing',
      'awaiting_payment',           // QR Ph payments waiting for customer to scan
      'awaiting_payment_method'     // Other payment methods
    ].includes(paymentResult.status);
    
    if (!shouldAutoPoll) return undefined;

    const timer = window.setInterval(() => {
      refreshPaymentStatus(true);
    }, 3000); // Check every 3 seconds instead of 5

    return () => window.clearInterval(timer);
  }, [isOpen, currentStep, paymentResult.bookingId, paymentResult.status, refreshPaymentStatus]);

  useEffect(() => {
    if (!paymentResult.bookingId || !paymentResult.status || paymentResult.status === 'paid') return;
    if (!paymentResult.paymentId && !paymentResult.qrCode && !paymentResult.checkoutUrl) return;
    if ((paymentResult.sessionSignature || '') === bookingDraftSignature) return;

    setPaymentResult({
      bookingId: null,
      paymentId: '',
      status: '',
      message: 'Booking details changed. Generate a new payment to match the updated services.',
      error: '',
      qrCode: '',
      qrLabel: '',
      checkoutUrl: '',
      sessionSignature: ''
    });
  }, [
    bookingDraftSignature,
    paymentResult.bookingId,
    paymentResult.status,
    paymentResult.paymentId,
    paymentResult.qrCode,
    paymentResult.checkoutUrl,
    paymentResult.sessionSignature
  ]);

  const handleSubmit = async () => {
    if (loading) return;

    if (currentStep === 'details') {
      if (!form.fullName || !form.contactNumber) { alert('Please fill in your name and contact number'); return; }
      if (normalizeContact(form.contactNumber).length !== 11) { alert('Contact number must be exactly 11 digits.'); return; }
      if (!isLoggedIn && !guestEmail) { alert('Please enter your email address.'); return; }
      if (!isLoggedIn && !isValidEmail(guestEmail)) { alert('Please enter a valid email address.'); return; }
      if (!form.branchId) { alert('Please select a branch'); return; }
      if (!form.preferredDate || !form.preferredTime) { alert('Please select your preferred date and time'); return; }
      if (!isTimeAllowedForSelectedBranch) {
        alert('Selected time is outside our working hours for the chosen branch and date. Please choose another slot.');
        return;
      }

      if (selectedServices.length === 0) { alert('Please select at least one service'); return; }
      setCurrentStep('payment');
      return;
    }

    if (currentStep === 'payment') {
      if (paymentResult.status === 'paid') {
        resetForm();
        onClose();
        return;
      }

      // If QR code is showing, just refresh status (don't regenerate)
      if (paymentResult.qrCode && selectedPaymentMethod === 'qrph') {
        await refreshPaymentStatus(false);
        return;
      }

      if (
        paymentResult.bookingId
        && ['awaiting_payment', 'awaiting_next_action', 'processing'].includes(paymentResult.status)
      ) {
        await refreshPaymentStatus(false);
        return;
      }

      if (!selectedPaymentMethod) { alert('Please select a payment method'); return; }

      setLoading(true);
      setPaymentResult(prev => ({ ...prev, error: '', message: '' }));
      try {
        const pendingPaymentId = paymentResult.bookingId || await createBookingRecord();
        const result = await processPayment(pendingPaymentId);

        setPaymentResult(prev => ({
          ...prev,
          bookingId: result.booking_id || result.pending_payment_id || pendingPaymentId,
          paymentId: result.payment_intent_id || result.checkout_session_id || result.source_id || prev.paymentId,
          status: result.status || prev.status,
          message: result.message || (result.status === 'paid' ? 'Payment completed successfully.' : prev.message),
          error: '',
          qrCode: result.qr_code || '',
          qrLabel: result.qr_label || '',
          checkoutUrl: result.checkout_url || '',
          sessionSignature: bookingDraftSignature
        }));

        if (result.checkout_url) {
          window.open(result.checkout_url, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error('[BookingModal] Payment generation failed:', {
          method: selectedPaymentMethod,
          code: error?.code || 'UNKNOWN',
          message: error?.message,
          status: error?.status,
          details: error
        });
        setPaymentResult(prev => ({
          ...prev,
          error: getUserFacingPaymentError(error, selectedPaymentMethod),
          message: ''
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  // Allow users to change services even if payment has started
  // const bookingLockedForPayment = Boolean(paymentResult.bookingId);
  const paymentCompleted = paymentResult.status === 'paid';
  const paymentButtonLabel = currentStep === 'details'
    ? 'Continue to Payment'
    : paymentCompleted
      ? 'Done'
      : selectedPaymentMethod === 'qrph'
          ? (paymentResult.qrCode ? 'Check Payment Status' : 'Generate QR Code')
          : 'Select Payment Method';
  const paymentStepDisabled = loading
    || !selectedPaymentMethod;

  if (!isOpen) return null;

  return (
    <>
      {showServiceSelector && isExternalMode && (
        <ServiceSelector
          onClose={() => setShowServiceSelector(false)}
          filteredServices={filteredServices}
          groupedServices={groupedFilteredServices}
          selectedServices={selectedServices}
          onToggle={(svc) => {
            selectedServices.find(s => s.id === svc.id)
              ? updateSelected(selectedServices.filter(s => s.id !== svc.id))
              : updateSelected([...selectedServices, svc]);
          }}
          totalAmount={totalAmount}
        />
      )}

      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-md p-0 sm:p-4">
        <div
          className="w-full sm:w-auto sm:max-w-[780px] bg-white rounded-t-[2rem] sm:rounded-[28px] shadow-[0_32px_80px_rgba(0,0,0,0.35)] overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95 duration-200 flex flex-col"
          style={{ maxHeight: '95svh', width: '100%', maxWidth: '780px' }}
        >
          <div className="flex justify-center pt-3 pb-1 sm:hidden bg-white flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-200" />
          </div>

          <OrderSummary
            branchSelected={branchSelected}
            selectedBranchLabel={selectedBranchLabel}
            selectedServices={selectedServices}
            form={form}
            totalAmount={totalAmount}
            totalDuration={totalDuration}
            selectedPaymentMethod={selectedPaymentMethod}
          />

          <div className="flex flex-col sm:flex-row flex-1 overflow-hidden" style={{ minHeight: 0 }}>

            {/* ── Left panel: Order summary (desktop only) ── */}
            <div className="hidden sm:flex w-64 flex-shrink-0 bg-[#1c0f07] flex-col" style={{ minHeight: 0 }}>
              <div className="px-6 pt-7 pb-6 border-b border-white/10">
                <p className="text-[9px] font-black uppercase tracking-[0.45em] text-[#c4956a]">Hygea Wellness</p>
                <h2 className="text-lg font-bold text-white mt-1 leading-tight">Your Appointment</h2>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" style={{ minHeight: 0 }}>
                {branchSelected && selectedBranchLabel && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Location</p>
                    <p className="text-sm font-semibold text-white/90 leading-snug">{selectedBranchLabel}</p>
                  </div>
                )}
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-2">Services</p>
                  {selectedServices.length === 0 ? (
                    <p className="text-xs text-white/30 italic">None selected yet</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedServices.map((s, i) => (
                        <div key={i} className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-white/85 leading-tight truncate">{s.name}</p>
                            {formatDuration(s.duration_minutes) && (
                              <p className="text-[10px] text-white/40 mt-0.5">{formatDuration(s.duration_minutes)}</p>
                            )}
                          </div>
                          <span className="text-xs font-bold text-[#c4956a] flex-shrink-0">₱{fmt(s.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(form.preferredDate || form.preferredTime) && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Schedule</p>
                    {form.preferredDate && <p className="text-xs font-semibold text-white/85">{new Date(form.preferredDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>}
                    {form.preferredTime && <p className="text-xs text-white/60 mt-0.5">{TIME_SLOTS.find(t => t.value === form.preferredTime)?.label || form.preferredTime}</p>}
                  </div>
                )}
                {form.numberOfClients > 1 && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Guests</p>
                    <p className="text-xs font-semibold text-white/85">{form.numberOfClients} persons</p>
                  </div>
                )}
                {selectedPaymentMethod && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Payment</p>
                    <p className="text-xs font-semibold text-white/85">
                      {PAYMENT_METHODS.find(m => m.value === selectedPaymentMethod)?.label}
                    </p>
                  </div>
                )}
                {totalDuration > 0 && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.35em] text-white/40 mb-1">Est. Duration</p>
                    <p className="text-xs font-semibold text-white/85">{formatDuration(totalDuration * form.numberOfClients)}</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-5 border-t border-white/10 flex-shrink-0">
                <div className="flex items-end justify-between">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">Total</p>
                  <p className="text-2xl font-black text-[#c4956a]">₱{fmt(totalAmount)}</p>
                </div>
                {form.numberOfClients > 1 && selectedServices.length > 0 && (
                  <p className="text-[10px] text-white/30 mt-1 text-right">
                    {form.numberOfClients} × ₱{fmt(selectedServices.reduce((s, sv) => s + Number(sv.price), 0))}
                  </p>
                )}
              </div>
            </div>

            {/* ── Right panel: Form ── */}
            <div className="flex-1 flex flex-col overflow-hidden bg-[#fdf8f3]" style={{ minHeight: 0 }}>
              <div className="px-5 sm:px-8 pt-5 sm:pt-7 pb-4 sm:pb-5 flex items-center justify-between border-b border-[#ede0d4] flex-shrink-0 bg-white">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.35em] text-[#c4956a]">
                    {currentStep === 'payment' ? 'Payment' : isLoggedIn ? 'Reserve Your Visit' : 'Guest Booking'}
                  </p>
                  <h3 className="text-lg sm:text-xl font-bold text-[#1a1009] mt-0.5">
                    {currentStep === 'payment' ? 'Complete Payment' : 'Book Appointment'}
                  </h3>
                </div>
                <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f5ede4] text-[#8b5e3c] flex items-center justify-center hover:bg-[#ecddd0] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6 space-y-5" style={{ minHeight: 0 }}>
                {currentStep === 'details' ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52] mb-2">Full Name</label>
                        <input
                          type="text"
                          value={form.fullName}
                          onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                          placeholder="Your name"
                          className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-white text-sm text-[#1a1009] placeholder-[#c4b0a0] outline-none focus:border-[#8b5e3c] focus:ring-2 focus:ring-[#8b5e3c]/15 transition-all"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52]">Contact No.</label>
                          {isLoggedIn && normalizeContact(user?.phone_number || user?.contact_number || user?.phone || '') && (
                            <span className="text-[9px] font-semibold text-[#c4956a] uppercase tracking-wider">From profile</span>
                          )}
                        </div>
                        <input
                          type="tel"
                          value={form.contactNumber}
                          onChange={e => setForm(p => ({ ...p, contactNumber: normalizeContact(e.target.value) }))}
                          placeholder="09XXXXXXXXX"
                          inputMode="numeric"
                          maxLength={11}
                          className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-white text-sm text-[#1a1009] placeholder-[#c4b0a0] outline-none focus:border-[#8b5e3c] focus:ring-2 focus:ring-[#8b5e3c]/15 transition-all"
                        />
                        {form.contactNumber.length > 0 && form.contactNumber.length < 11 && (
                          <p className="text-[10px] text-amber-500 mt-1">{11 - form.contactNumber.length} more digit{11 - form.contactNumber.length !== 1 ? 's' : ''} needed</p>
                        )}
                      </div>
                    </div>

                    {!isLoggedIn && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52]">
                            Email Address
                            <span className="ml-1.5 text-[#c4956a] normal-case tracking-normal font-semibold">(required)</span>
                          </label>
                        </div>
                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c4a894] pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                            </svg>
                          </span>
                          <input
                            type="email"
                            value={form.guestEmail}
                            onChange={e => setForm(p => ({ ...p, guestEmail: e.target.value }))}
                            placeholder="you@example.com"
                            required={!isLoggedIn}
                            className={`w-full pl-10 pr-4 py-3 sm:py-2.5 rounded-xl border text-sm text-[#1a1009] placeholder-[#c4b0a0] bg-white outline-none transition-all focus:ring-2 ${
                              guestEmailMissing || emailError
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                                : 'border-[#e8d9cc] focus:border-[#8b5e3c] focus:ring-[#8b5e3c]/15'
                            }`}
                          />
                        </div>
                        {guestEmailMissing && <p className="text-[10px] text-red-400 mt-1">Email address is required for guest bookings.</p>}
                        {!guestEmailMissing && emailError && <p className="text-[10px] text-red-400 mt-1">Please enter a valid email address.</p>}
                        <p className="text-[10px] text-[#c4a894] mt-1.5">We'll send your booking confirmation here.</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52] mb-2">Branch</label>
                      {defaultBranchId ? (
                        <div className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-[#faf4ee] text-sm text-[#6b4e38] font-semibold">
                          {selectedBranchLabel || 'Selected branch'}
                        </div>
                      ) : (
                        <select
                          value={form.branchId}
                          onChange={e => setForm(p => ({ ...p, branchId: e.target.value }))}
                          className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-white text-sm text-[#1a1009] outline-none focus:border-[#8b5e3c] focus:ring-2 focus:ring-[#8b5e3c]/15 transition-all appearance-none"
                        >
                          <option value="">Choose a branch…</option>
                          {branches.map(b => (
                            <option key={b.branch_id} value={b.branch_id}>
                              {b.branch_name}{b.address ? ` — ${b.address}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8b5e3c]">Services</label>
                        {isExternalMode && branchSelected && filteredServices.length > 0 && (
                          <button type="button" onClick={() => setShowServiceSelector(true)}
                            className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8b5e3c] hover:text-[#6a4428] transition-colors flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                            </svg>
                            {selectedServices.length > 0 ? 'Change' : 'Select'}
                          </button>
                        )}
                      </div>
                      {selectedServices.length === 0 ? (
                        <div
                          onClick={() => isExternalMode && branchSelected && setShowServiceSelector(true)}
                          className={`w-full rounded-xl border-2 border-dashed border-[#dcc9b8] p-4 text-center transition-all ${isExternalMode && branchSelected ? 'cursor-pointer hover:border-[#8b5e3c] hover:bg-[#fdf0e4]' : 'opacity-50'}`}
                        >
                          <p className="text-xs text-[#a89080]">
                            {!branchSelected ? 'Select a branch first' : 'Tap to choose services'}
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-[#e8d9cc] bg-white divide-y divide-[#f5ede4] overflow-hidden">
                          {selectedServices.map((s, i) => (
                            <div key={i} className="flex items-center justify-between px-4 py-3 sm:py-2.5">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-[#1a1009] truncate">{s.name}</p>
                                {s.category_name && (
                                  <p className="text-[10px] text-[#c4956a] font-bold uppercase tracking-wider mt-0.5">{s.category_name}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                                <span className="text-sm font-bold text-[#8b5e3c]">₱{fmt(s.price)}</span>
                                <button type="button" onClick={() => updateSelected(selectedServices.filter(sv => sv.id !== s.id))}
                                  className="w-6 h-6 rounded-full bg-[#f5ede4] text-[#a87060] flex items-center justify-center hover:bg-red-100 hover:text-red-500 transition-colors"
                                >
                                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52] mb-2">Date</label>
                        <input type="date" value={form.preferredDate} min={new Date().toISOString().split('T')[0]}
                          onChange={e => setForm(p => ({ ...p, preferredDate: e.target.value }))}
                          disabled={!canProceed}
                          className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-white text-sm text-[#1a1009] outline-none focus:border-[#8b5e3c] focus:ring-2 focus:ring-[#8b5e3c]/15 transition-all disabled:bg-[#f5f0eb] disabled:text-[#c4b0a0] disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52] mb-2">Time</label>
                        <select value={form.preferredTime} onChange={e => setForm(p => ({ ...p, preferredTime: e.target.value }))}
                          disabled={!canProceed}
                          className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-[#e8d9cc] bg-white text-sm text-[#1a1009] outline-none focus:border-[#8b5e3c] focus:ring-2 focus:ring-[#8b5e3c]/15 transition-all disabled:bg-[#f5f0eb] disabled:text-[#c4b0a0] disabled:cursor-not-allowed appearance-none"
                        >
                          <option value="">Select…</option>
                          {allowedTimeSlots.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-[#8b6b52] mb-2">Guests</label>
                        <div className="flex items-center border border-[#e8d9cc] bg-white rounded-xl overflow-hidden">
                          <button type="button" onClick={() => setForm(p => ({ ...p, numberOfClients: Math.max(1, p.numberOfClients - 1) }))}
                            disabled={!canProceed}
                            className="w-11 h-11 sm:h-10 flex items-center justify-center text-[#8b5e3c] hover:bg-[#fdf0e4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="flex-1 text-center text-sm font-black text-[#1a1009]">{form.numberOfClients}</span>
                          <button type="button" onClick={() => setForm(p => ({ ...p, numberOfClients: p.numberOfClients + 1 }))}
                            disabled={!canProceed}
                            className="w-11 h-11 sm:h-10 flex items-center justify-center text-[#8b5e3c] hover:bg-[#fdf0e4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <PaymentStep
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    totalAmount={totalAmount}
                    paymentAmount={paymentAmount}
                    processingPayment={loading && currentStep === 'payment'}
                    paymentResult={paymentResult}
                    onRefreshPaymentStatus={() => refreshPaymentStatus(false)}
                    onRetryQrPh={handleSubmit}
                    onClose={onClose}
                  />
                )}
              </div>

              {/* Sticky action bar */}
              <div className="flex-shrink-0 border-t border-[#ede0d4] bg-white px-5 sm:px-8 py-4 flex items-center gap-3">
                {currentStep === 'payment' && paymentCompleted ? (
                  <button type="button" onClick={onClose}
                    className="px-4 sm:px-5 py-3 rounded-2xl border border-[#dcc9b8] text-sm font-bold text-[#8b6b52] hover:bg-[#fdf0e4] transition-colors flex-shrink-0"
                  >
                    Done
                  </button>
                ) : currentStep === 'payment' ? (
                  <button type="button" onClick={() => setCurrentStep('details')}
                    disabled={paymentCompleted}
                    className="px-4 sm:px-5 py-3 rounded-2xl border border-[#dcc9b8] text-sm font-bold text-[#8b6b52] hover:bg-[#fdf0e4] transition-colors flex-shrink-0 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                ) : (
                  <button type="button" onClick={onClose}
                    className="px-4 sm:px-5 py-3 rounded-2xl border border-[#dcc9b8] text-sm font-bold text-[#8b6b52] hover:bg-[#fdf0e4] transition-colors flex-shrink-0"
                  >
                    Cancel
                  </button>
                )}
                <button 
                  type="button" 
                  onClick={() => {
                    if (paymentCompleted) {
                      onClose();
                      return;
                    }

                    // If QR is showing, just refresh payment status
                    if (paymentResult.qrCode && selectedPaymentMethod === 'qrph') {
                      refreshPaymentStatus(false);
                    } else {
                      handleSubmit();
                    }
                  }}
                  disabled={
                    currentStep === 'details'
                      ? (loading || !canProceed || !form.preferredDate || !form.preferredTime || !form.fullName || !form.contactNumber || guestEmailMissing || emailError)
                      : paymentStepDisabled
                  }
                  className="flex-1 py-3 rounded-2xl bg-[#8b5e3c] text-white font-black text-sm uppercase tracking-[0.15em] hover:bg-[#7a5234] transition-colors shadow-lg shadow-[#8b5e3c]/25 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{currentStep === 'payment' ? (paymentResult.qrCode && selectedPaymentMethod === 'qrph' ? 'Checking Payment...' : (selectedPaymentMethod === 'qrph' ? 'Generating QR Code...' : 'Processing Payment...')) : 'Processing...'}</>
                  ) : paymentButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingModal;
